import '../../styles/AdminStyles/RegistrationsPanel.css';
import { EmailDelivery, Registration, RegistrationFilters, RegistrationStatus, REGISTRATION_STATUS_LABELS, Webinar } from "../../webinars/types";
import { webinarRepository } from "../../webinars/repository";
import { useRepositoryData } from "../../webinars/useRepositoryData";
import { downloadCsv, registrationsToCsv } from "../../webinars/csv";
import { DeliveriesByRegistration, deliveryLabel, deliveryTone, hasEmailIssue, hasJoiningInstructions } from "../../webinars/emailStatus";
import { formatDateTime, formatShortDate, formatTime } from "../../webinars/format";
import { IS_PROTOTYPE } from "../../webinars/config";
import { useMediaQuery } from "../../hooks/useMediaQuery";

interface RegistrationsPanelProps {
  webinars: Webinar[];
  hasAnyRegistrations: boolean;
  deliveries: DeliveriesByRegistration;
  filters: RegistrationFilters;
  onFiltersChange: (filters: RegistrationFilters) => void;
  onOpen: (registrationId: string) => void;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  onJoiningInstructions: (webinarId: string) => void;
  /** Opens "Prepare a manual message" for the selected attendees of this webinar */
  onPrepareManual: (webinarId: string) => void;
  onAddSamples: () => void;
  notify: (message: string) => void;
}

/** Below this width attendees are shown as cards rather than a table */
const PHONE = "(max-width: 760px)";

type Tab = "all" | RegistrationStatus | "issues";

function EmailStatus({ label, delivery }: { label: string; delivery?: EmailDelivery }) {
  return (
    <span className={`wa-mail wa-mail--${deliveryTone(delivery)}`} title={delivery?.lastError}>
      {label}: {deliveryLabel(delivery)}{delivery?.complainedAt ? " · marked as spam" : ""}
    </span>
  );
}

/** Registered / Cancelled / Attended, plus Archived and "sent by hand" when they apply */
function StatusChips({ r }: { r: Registration }) {
  return (
    <>
      <span className={`wa-chip wa-chip--reg-${r.status}`}>{REGISTRATION_STATUS_LABELS[r.status]}</span>
      {r.archivedAt && (
        <span className="wa-chip wa-chip--archived" title={`Archived ${formatDateTime(r.archivedAt)} SAST, when the webinar was reopened`}>
          Archived{r.roundStartAt ? ` · ${formatShortDate(r.roundStartAt)} round` : ""}
        </span>
      )}
      {r.instructionsSentAt && <span className="wa-sent" title="Joining instructions recorded as sent by hand">✓ Sent by hand</span>}
    </>
  );
}

// The attendee list: counts, status tabs, then a compact table on desktop (phone, organisation and
// role are in the attendee's record) or one card per attendee on phones. Both share the filters,
// selection and bulk actions.
export default function RegistrationsPanel({
  webinars, hasAnyRegistrations, deliveries, filters, onFiltersChange, onOpen,
  selectedIds, onSelectionChange, onJoiningInstructions, onPrepareManual, onAddSamples, notify,
}: RegistrationsPanelProps) {
  // The repository filters by webinar, round and search (in SQL for Supabase). The status tabs
  // filter that list here, so they can show a count for every tab without reloading.
  const loadFilters: RegistrationFilters = { ...filters, status: undefined, emailIssues: undefined };
  const { data: loaded = [], loading } = useRepositoryData(
    () => webinarRepository.listRegistrations(loadFilters),
    JSON.stringify(loadFilters),
  );
  const isPhone = useMediaQuery(PHONE);
  const webinarsById = Object.fromEntries(webinars.map(w => [w.id, w]));
  const showingArchived = filters.round === "archived";
  const oneWebinar = filters.webinarId ? webinarsById[filters.webinarId] : undefined;
  const oneWebinarCancelled = oneWebinar?.status === "cancelled";
  // Every row would say the same webinar
  const showWebinar = !filters.webinarId;

  // ── Counts and tabs ──
  const active = loaded.filter(r => r.status !== "cancelled");
  const withIssue = (r: Registration) => r.status !== "cancelled" && hasEmailIssue(r, deliveries);
  const byStatus = (status: RegistrationStatus) => loaded.filter(r => r.status === status).length;
  const tabs: { key: Tab; label: string; count: number }[] = [
    { key: "all", label: "All attendees", count: loaded.length },
    { key: "registered", label: "Registered", count: byStatus("registered") },
    { key: "attended", label: "Attended", count: byStatus("attended") },
    { key: "cancelled", label: "Cancelled", count: byStatus("cancelled") },
    { key: "issues", label: "Email issues", count: active.filter(withIssue).length },
  ];
  const tab: Tab = filters.emailIssues ? "issues" : filters.status ?? "all";
  const rows = loaded.filter(r =>
    tab === "all" ? true : tab === "issues" ? withIssue(r) : r.status === tab);
  const confirmed = active.filter(r => deliveryTone(deliveries[r.id]?.confirmation) === "done").length;
  const issues = tabs[4].count;

  const isFiltered = Boolean(filters.search?.trim() || filters.webinarId || tab !== "all" || showingArchived);

  // ── Selection: within one webinar, for the current round, never for a cancelled webinar ──
  const selecting = Boolean(filters.webinarId) && !showingArchived && !oneWebinarCancelled;
  const canSelect = (r: Registration) => r.status === "registered" && !r.archivedAt;
  const selectable = rows.filter(canSelect);
  const allSelected = selectable.length > 0 && selectable.every(r => selectedIds.includes(r.id));
  const selectAll = () => onSelectionChange(allSelected ? [] : selectable.map(r => r.id));
  const selected = loaded.filter(r => selectedIds.includes(r.id));
  const alreadyHave = selected.filter(r => hasJoiningInstructions(r, deliveries)).length;

  const update = (patch: Partial<RegistrationFilters>) => {
    if (patch.webinarId !== undefined && patch.webinarId !== filters.webinarId) onSelectionChange([]);
    onFiltersChange({ ...filters, ...patch });
  };
  const showTab = (key: Tab) =>
    update(key === "issues" ? { status: undefined, emailIssues: true }
      : { status: key === "all" ? undefined : key, emailIssues: undefined });

  const toggle = (id: string) =>
    onSelectionChange(selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id]);

  const exportCsv = async (scope: "filtered" | "all") => {
    const exportRows = scope === "all" ? await webinarRepository.listRegistrations({ sort: filters.sort, round: "all" }) : rows;
    const webinar = scope === "filtered" && filters.webinarId ? webinarsById[filters.webinarId] : undefined;
    const date = new Date().toISOString().slice(0, 10);
    downloadCsv(
      registrationsToCsv(exportRows, webinarsById, deliveries),
      `kole-webinar-registrations${webinar ? `-${webinar.slug}` : scope === "filtered" && isFiltered ? "-filtered" : ""}-${date}.csv`,
    );
    notify(scope === "all" ? "Exported all registrations" : `Exported ${exportRows.length} registration${exportRows.length === 1 ? "" : "s"}`);
  };

  const fullName = (r: Registration) => `${r.firstName} ${r.lastName}`;
  const webinarCell = (r: Registration) => {
    const webinar = webinarsById[r.webinarId];
    return (
      <>
        {webinar ? webinar.title : <em>Unknown webinar</em>}
        {webinar?.status === "cancelled" && <span className="wa-chip wa-chip--cancelled">Event cancelled</span>}
      </>
    );
  };
  const checkbox = (r: Registration) => canSelect(r) && (
    <input type="checkbox" aria-label={`Select ${fullName(r)}`} checked={selectedIds.includes(r.id)} onChange={() => toggle(r.id)} />
  );

  return (
    <section className="wa-panel" id="attendees" aria-labelledby="wa-attendees-title">
      <div className="wa-panel-head">
        <h2 id="wa-attendees-title">Attendees</h2>
        <div className="wa-export">
          <button type="button" className="wa-btn wa-btn--export" onClick={() => exportCsv("filtered")} disabled={rows.length === 0}>
            Export {isFiltered ? "filtered results" : "list"} (CSV)
          </button>
          {isFiltered && (
            <button type="button" className="wa-btn" onClick={() => exportCsv("all")} disabled={!hasAnyRegistrations}>Export all (CSV)</button>
          )}
        </div>
      </div>

      {hasAnyRegistrations && (
        <dl className="wa-att-summary" aria-label={oneWebinar ? `Counts for ${oneWebinar.title}` : "Counts for this list"}>
          <div><dt>Registrations</dt><dd>{active.length}</dd></div>
          <div><dt>Confirmed</dt><dd>{confirmed}</dd></div>
          <div className={issues ? "is-issue" : ""}><dt>Email issues</dt><dd>{issues}</dd></div>
        </dl>
      )}

      <div className="wa-filters">
        <label className="wa-filter wa-filter--search">
          <span className="km-visuallyHidden">Search attendees</span>
          <input
            type="search"
            placeholder="Search name, email or registration ID…"
            value={filters.search ?? ""}
            onChange={e => update({ search: e.target.value })}
          />
        </label>
        <label className="wa-filter">
          <span className="km-visuallyHidden">Filter by webinar</span>
          <select value={filters.webinarId ?? ""} onChange={e => update({ webinarId: e.target.value || undefined })}>
            <option value="">All webinars</option>
            {[...webinars]
              .sort((a, b) => Date.parse(b.startAt) - Date.parse(a.startAt))
              .map(w => <option key={w.id} value={w.id}>{formatShortDate(w.startAt)} · {w.title}</option>)}
          </select>
        </label>
        <label className="wa-filter">
          <span className="km-visuallyHidden">Current or archived attendees</span>
          <select value={filters.round ?? "current"} onChange={e => update({ round: e.target.value === "archived" ? "archived" : undefined })}>
            <option value="current">Current attendees</option>
            <option value="archived">Archived attendees</option>
          </select>
        </label>
        <label className="wa-filter">
          <span className="km-visuallyHidden">Sort by registration date</span>
          <select value={filters.sort ?? "newest"} onChange={e => update({ sort: e.target.value as "newest" | "oldest" })}>
            <option value="newest">Newest registrations first</option>
            <option value="oldest">Oldest registrations first</option>
          </select>
        </label>
      </div>

      <div className="wa-status-tabs" role="group" aria-label="Show attendees">
        {tabs.map(t => (
          <button
            key={t.key}
            type="button"
            aria-pressed={tab === t.key}
            className={t.key === "issues" && t.count > 0 ? "is-issue" : undefined}
            onClick={() => showTab(t.key)}
          >
            {t.label} <span className="wa-tab-count">{t.count}</span>
          </button>
        ))}
      </div>

      <p className="wa-count" role="status">
        {loading && loaded.length === 0 ? "Loading…" : `Showing ${rows.length} registration${rows.length === 1 ? "" : "s"}`}
        {oneWebinar && <> for <strong>{oneWebinar.title}</strong></>}
        {isFiltered && (
          <button type="button" className="wa-link" onClick={() => { onSelectionChange([]); onFiltersChange({ sort: filters.sort }); }}>Clear filters</button>
        )}
      </p>

      {oneWebinarCancelled && (
        <p className="wa-warning wa-cancelled-note" role="note">
          This webinar is cancelled, so joining instructions can't be sent. Tell the registered attendees directly.
        </p>
      )}

      {selecting && selectedIds.length > 0 && (
        <div className="wa-selection-bar" role="region" aria-label="Selected attendees">
          <span className="wa-selection-count">
            {selectedIds.length} attendee{selectedIds.length === 1 ? "" : "s"} selected
            {alreadyHave > 0 && <small> · {alreadyHave} already {alreadyHave === 1 ? "has" : "have"} the joining instructions</small>}
          </span>
          <button type="button" className="wa-link" onClick={() => onSelectionChange([])}>Clear selection</button>
          <span className="wa-selection-actions">
            <button type="button" className="km-btn km-btn--primary" onClick={() => onJoiningInstructions(filters.webinarId!)}>Send joining instructions</button>
            <button type="button" className="wa-btn wa-btn--quiet" onClick={() => onPrepareManual(filters.webinarId!)}>Prepare manual message</button>
          </span>
        </div>
      )}

      {!hasAnyRegistrations ? (
        <div className="wa-empty">
          <p>No registrations yet.{IS_PROTOTYPE ? " Registrations made on the webinar pages in this browser appear here." : ""}</p>
          {IS_PROTOTYPE && <button type="button" className="wa-btn" onClick={onAddSamples}>Add sample registrations</button>}
        </div>
      ) : rows.length === 0 && !loading ? (
        <p className="wa-empty">
          {tab === "issues" ? "No email issues: every email so far was sent or is waiting to be sent."
            : showingArchived ? "No archived registrations match. Registrations are archived when a webinar is reopened for a new round."
            : "No registrations match these filters."}
        </p>
      ) : isPhone ? (
        // ── Phones: one card per attendee ──
        <>
          {selecting && selectable.length > 0 && (
            <label className="wa-check wa-cards-select-all">
              <input type="checkbox" checked={allSelected} onChange={selectAll} />
              Select everyone registered ({selectable.length})
            </label>
          )}
          <ul className="wa-cards">
            {rows.map(r => {
              const mail = deliveries[r.id] ?? {};
              return (
                <li key={r.id} className={`wa-att-card${selectedIds.includes(r.id) ? " is-selected" : ""}`}>
                  <div className="wa-att-card-head">
                    {selecting && <span className="wa-att-card-check">{checkbox(r)}</span>}
                    <div className="wa-att-card-who">
                      <strong>{fullName(r)}</strong>
                      <span className="wa-ref">{r.reference}</span>
                    </div>
                  </div>
                  <p className="wa-att-card-email">{r.email}</p>
                  {showWebinar && <p className="wa-att-card-webinar">{webinarCell(r)}</p>}
                  <div className="wa-att-card-chips">
                    <StatusChips r={r} />
                    <span className="wa-att-card-date">{formatShortDate(r.registeredAt)}</span>
                  </div>
                  <div className="wa-att-card-mail">
                    <EmailStatus label="Confirmation" delivery={mail.confirmation} />
                    <EmailStatus label="Joining" delivery={mail.joining_instructions} />
                  </div>
                  <button type="button" className="wa-btn wa-att-card-view" onClick={() => onOpen(r.id)}>
                    View attendee details<span className="km-visuallyHidden">: {fullName(r)}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      ) : (
        // ── Desktop: compact table ──
        <div className="wa-table-scroll wa-table-scroll--tall">
          <table className={`wa-table wa-table--registrations${showWebinar ? "" : " wa-table--one-webinar"}`}>
            <thead>
              <tr>
                {selecting && (
                  <th scope="col" className="wa-select-col">
                    <input type="checkbox" aria-label="Select everyone registered" checked={allSelected} disabled={selectable.length === 0} onChange={selectAll} />
                  </th>
                )}
                <th scope="col">Attendee</th>
                {showWebinar && <th scope="col">Webinar</th>}
                <th scope="col">Registered</th>
                <th scope="col">Status</th>
                <th scope="col">Emails</th>
                <th scope="col"><span className="km-visuallyHidden">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const mail = deliveries[r.id] ?? {};
                return (
                  <tr key={r.id} className="wa-row-clickable" onClick={() => onOpen(r.id)}>
                    {selecting && <td className="wa-select-col" onClick={e => e.stopPropagation()}>{checkbox(r)}</td>}
                    <td className="wa-attendee-cell">
                      <strong className="wa-name">{fullName(r)}</strong>
                      <small><span className="wa-ref">{r.reference}</span> · {r.email}</small>
                    </td>
                    {showWebinar && <td className="wa-webinar-cell">{webinarCell(r)}</td>}
                    <td className="wa-nowrap wa-date-cell">
                      {formatShortDate(r.registeredAt)}
                      <small>{formatTime(r.registeredAt)}</small>
                    </td>
                    <td className="wa-status-cell"><StatusChips r={r} /></td>
                    <td className="wa-mail-cell">
                      <EmailStatus label="Confirmation" delivery={mail.confirmation} />
                      <EmailStatus label="Joining" delivery={mail.joining_instructions} />
                    </td>
                    <td>
                      <button type="button" className="wa-btn" onClick={e => { e.stopPropagation(); onOpen(r.id); }}>
                        View<span className="km-visuallyHidden"> {fullName(r)}</span>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
